import axios from 'axios';
// import Axios from 'axios';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { ACTIONS } from '../utils/actions';
import { IItem } from '../utils/commonInterfaces';

import { ItemTypes } from '../utils/items';
import { move } from '../utils/move';
import { getDescribeTableLink } from '../utils/storageHelpers';
import { loadTablesFromS3 } from '../utils/s3TableLoader';
import {
  useGlobal,
  ICognitoUser,
  ICognitoClient,
  ICountry,
  IAgency,
} from './provider';

// Access environment variable directly instead of destructuring at module level
// This ensures Vite's define replacements work correctly

interface IClient {
  agency: string;
  selected: boolean;
  client_name: string;
  client_code: string;
  country: string;
}

interface IClientInfo {
  clientCode: { client: string; country: string };
  clientName: string;
  agency: string;
}
interface IQueryClient {
  client: string;
  country: string;
}

interface ITable {
  reset?: boolean;
  selected: boolean;
  id: string;
  caption: string;
  source: string;
  view: string;
  group: string;
}

interface ICheckedTable {
  datatype: string;
  name: string;
  checked: boolean;
  dimension: boolean;
  filter: boolean;
  filterList: Set<unknown>;
  metric: {
    include: boolean;
    pretty: string;
    method: string;
  };
  filterInfo: {
    include: boolean;
    filtertype: string;
    datatype: string;
    value: string;
  };
}

interface IView {
  viewName: string;
}

interface IUser {
  agency: string;
  country: string;
}

interface IApiMetric {
  aggregation: string;
  datatype: string;
  column: string;
  name: string;
}

interface IId {
  id: string;
}

interface INewMetric extends IId {
  datatype: string;
  aggregationMethod: string;
  column: string;
  name: string;
  itemType: string;
}

export interface IFilterValue {
  datatype: string;
  filtertype: string;
  value: string;
}

interface INewFilter extends IId {
  datatype: string;
  filter: IFilterValue;
  column: string;
  itemType: string;
}
interface INewDimension extends IId {
  datatype: string;
  dimension: string;
  name: string;
  itemType: string;
}

interface IApiQuery {
  metrics: IApiMetric[];
  dimensions: string[];
  filters: INewFilter[];
}

export interface IUpdatedQuery {
  views: IView[];
  clients: IQueryClient[];
  query: IApiQuery;
  user: IUser;
}
interface IQueryBuilderContextState {
  addMetric: (item: { datatype: string; column: string }, id: string) => void;
  queryAlert: {
    showAlert: boolean;
    alertDescription: string;
    alertTitle: string;
  };
  resetQueryAlert: () => void;
  metricItems: INewMetric[];
  checkedItems: ICheckedTable[];
  clients: IClient[];
  dimItems: INewDimension[];
  filterItems: INewFilter[];
  addDimension: (dim: string) => void;
  addFilter: (item: IItem, id: string) => void;
  removeDimensions: (id: string, itemType: string) => void;
  findColumn: (id: string) => ICheckedTable;
  GetTableColumnsFromStorage: (table: ITable) => Promise<void>;
  getDataBaseByCountry: (country: string) => unknown;
  updateMetric: (val: string, id: string, itemMethod: string) => void;
  updateFilter: (val: string, id: string, itemMethod: string) => void;
  moveCard: (droppedId: string, originalIdx: number, itemType: string) => void;
  resetItems: unknown;
  updateQuery: (query: IUpdatedQuery) => void;
  getQueryInfo: unknown;
  newQueryInput: unknown;
  isNotReady: boolean;
  findIndexById: (indexType: string, id: string) => number | null;
  tables: ITable[];
  selectedAgency: IAgency;
  selectedCountry: ICountry;
  selectedTable: { table: ITable; reset: boolean };
  selectedClients: IClient[];
  selectedDates: unknown;
  setSelectedDates: (dates: unknown) => void;
  userSignIn: (user: ICognitoUser) => void;
  toggleClient: (clientCode: string, country: string) => void;
  UpdateAvailableClients: (
    clients: IClient[],
    country: string,
  ) => IClient[];
  selectNewTable: (id: string, resetCols: boolean) => void;
  signInFrom: string;
  signedInFrom: () => void;
  allowEmptyClientList: boolean;
  setAllowEmptyClientList: (value: boolean) => void;
}

const QueryBuilderContext = createContext<IQueryBuilderContextState>(
  {} as IQueryBuilderContextState,
);

function alert(arg0: string) {
  throw new Error(arg0);
}

export function useQueryBuilder() {
  return useContext(QueryBuilderContext);
}

const newQueryInput = {
  query: {
    preview: 20,
  },
  views: [
    {
      viewName: 'notset',
      database: 'notset',
    },
  ],
  jobnumber: 'job01',
  clients: [],
  date: {},
  user: {
    name: 'notset',
    agency: 'notset',
    country: 'notset',
  },
};

// Comprehensive default tables with multiple groups for better user experience
const comprehensiveDefaultTables = [
  // Adform group
  {
    id: '1',
    caption: 'adform',
    source: 'adform.csv',
    view: 'dl_adform_',
    group: 'adform',
    selected: true,
  },
  {
    id: '2',
    caption: 'tracking data',
    source: 'adform_tracking_data.csv',
    view: 'dl_adform_tracking_data_',
    group: 'adform',
    selected: false,
  },

  // Facebook group
  {
    id: '3',
    caption: 'facebook ads',
    source: 'facebook_ads.csv',
    view: 'dl_facebook_ads_',
    group: 'facebook',
    selected: false,
  },
  {
    id: '4',
    caption: 'facebook insights',
    source: 'facebook_insights.csv',
    view: 'dl_facebook_insights_',
    group: 'facebook',
    selected: false,
  },

  // Google group
  {
    id: '5',
    caption: 'google ads',
    source: 'google_ads.csv',
    view: 'dl_google_ads_',
    group: 'google',
    selected: false,
  },
  {
    id: '6',
    caption: 'google analytics',
    source: 'google_analytics.csv',
    view: 'dl_google_analytics_',
    group: 'google',
    selected: false,
  },
  {
    id: '7',
    caption: 'google search console',
    source: 'google_search_console.csv',
    view: 'dl_google_search_console_',
    group: 'google',
    selected: false,
  },

  // DV360 group
  {
    id: '8',
    caption: 'dv360 campaigns',
    source: 'dv360_campaigns.csv',
    view: 'dl_dv360_campaigns_',
    group: 'dv360',
    selected: false,
  },
  {
    id: '9',
    caption: 'dv360 creative',
    source: 'dv360_creative.csv',
    view: 'dl_dv360_creative_',
    group: 'dv360',
    selected: false,
  },

  // Amazon group
  {
    id: '10',
    caption: 'amazon dsp',
    source: 'amazon_dsp.csv',
    view: 'dl_amazon_dsp_',
    group: 'amazon',
    selected: false,
  },
  {
    id: '11',
    caption: 'amazon advertising',
    source: 'amazon_advertising.csv',
    view: 'dl_amazon_advertising_',
    group: 'amazon',
    selected: false,
  },

  // TikTok group
  {
    id: '12',
    caption: 'tiktok ads',
    source: 'tiktok_ads.csv',
    view: 'dl_tiktok_ads_',
    group: 'tiktok',
    selected: false,
  },

  // LinkedIn group
  {
    id: '13',
    caption: 'linkedin ads',
    source: 'linkedin_ads.csv',
    view: 'dl_linkedin_ads_',
    group: 'linkedin',
    selected: false,
  },

  // TV & Audio group
  {
    id: '14',
    caption: 'tv viewership',
    source: 'tv_viewership.csv',
    view: 'dl_tv_viewership_',
    group: 'tv_audio',
    selected: false,
  },
  {
    id: '15',
    caption: 'radio listening',
    source: 'radio_listening.csv',
    view: 'dl_radio_listening_',
    group: 'tv_audio',
    selected: false,
  },

  // E-commerce group
  {
    id: '16',
    caption: 'shopify sales',
    source: 'shopify_sales.csv',
    view: 'dl_shopify_sales_',
    group: 'ecommerce',
    selected: false,
  },
  {
    id: '17',
    caption: 'magento orders',
    source: 'magento_orders.csv',
    view: 'dl_magento_orders_',
    group: 'ecommerce',
    selected: false,
  },
];

export const QueryBuilderProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const {
    countries,
    agencies,
    userName,
    selectAgency,
    selectCountry,
    allClients,
    signIn,
    loggedIn,
  } = useGlobal();

  const [tables, setTables] = useState(comprehensiveDefaultTables);
  const [clients, setClients] = useState<IClient[]>([]);
  // const [, setDatabase] = useState('data_lake');
  const [metricItems, setMetricItems] = useState<INewMetric[]>([]);
  const [checkedItems, setCheckedItems] = useState<ICheckedTable[]>([]);
  const [dimItems, setDimItems] = useState<INewDimension[]>([]);
  const [filterItems, setFilterItems] = useState<INewFilter[]>([]);
  // Initialize selectedDates with default range: January 1st of current year to today
  const [selectedDates, setSelectedDates] = useState<unknown>({
    from: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'), // January 1st of current year
    to: format(new Date(), 'yyyy-MM-dd'), // Today
  });

  const initAlert = useMemo(
    () => ({
      showAlert: false,
      alertDescription: '',
      alertTitle: '',
    }),
    [],
  );
  const [queryAlert, setQueryAlert] = useState(initAlert);

  const selectedClients = useMemo(() => {
    const newSelected = clients.filter((a) => a.selected);
    // console.log('selected clients called', newSelected);
    return newSelected;
  }, [clients]);
  // useRef([]);

  const [signInFrom, setSignInFrom] = useState('NOTSET');

  const resetQueryAlert = useCallback(() => {
    setQueryAlert(() => initAlert);
  }, [initAlert]);

  const resetItems = () => {
    setMetricItems([]);
    setDimItems([]);
    setFilterItems([]);
  };

  function convertDataType(dataType: string) {
    const tmpDataType = dataType.toLowerCase().startsWith('varchar')
      ? 'string'
      : dataType.toLowerCase();
    switch (tmpDataType) {
      case 'string':
        return 'STRING';
      case 'date':
        return 'DATE';
      default:
        return 'NUMBER';
    }
  }

  const GetTableColumnsFromStorage = useCallback(async (table: ITable) => {
    // Loading columns for table

    try {
      // Get the new table data
      const link = await getDescribeTableLink(table.source);

      if (!link) {
        console.warn(
          `Could not get storage link for ${table.source}, using default table structure`,
        );
        // Provide a minimal default table structure when storage is not available
        const defaultTableItems: ICheckedTable[] = [
          {
            datatype: 'STRING',
            name: 'default_column',
            checked: false,
            dimension: false,
            filter: false,
            filterList: new Set(),
            metric: {
              include: false,
              pretty: 'Default Column',
              method: 'SUM',
            },
            filterInfo: {
              include: false,
              filtertype: 'EQUAL',
              datatype: 'STRING',
              value: '',
            },
          },
        ];
        setCheckedItems(defaultTableItems);
        return;
      }

      // Got S3 link, fetching column data directly

      // Direct fetch approach (no Papa Parse)
      const response = await fetch(link);

      if (!response.ok) {
        console.error(
          `❌ GetTableColumnsFromStorage: HTTP ${response.status} response`,
        );
        throw new Error(`HTTP ${response.status}`);
      }

      const csvContent = await response.text();

      // Check for HTML content (error response)
      if (
        csvContent.includes('<!DOCTYPE html>') ||
        csvContent.includes('<html')
      ) {
        console.error(
          `❌ GetTableColumnsFromStorage: Received HTML content instead of CSV`,
        );
        throw new Error('HTML content received');
      }

      // Parse CSV content manually
      const lines = csvContent.trim().split('\n');
      const columnData: { column: string; dataType: string }[] = [];

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        // Split by comma and take first two values
        const parts = trimmedLine.split(',');
        if (parts.length >= 2) {
          const column = parts[0].trim();
          const dataType = parts[1].trim();
          if (column) {
            columnData.push({ column, dataType });
          }
        }
      }

      console.log(
        `📋 GetTableColumnsFromStorage: Parsed ${columnData.length} columns`,
      );

      const tableItems = columnData.map((col) => {
        const datatype = convertDataType(col.dataType);
        const metricMethod = datatype === 'DATE' ? 'MAX' : 'SUM';
        return {
          datatype,
          name: col.column,
          checked: false,
          dimension: false,
          filter: false,
          filterList: new Set(),
          metric: {
            include: false,
            pretty: col.column,
            method: metricMethod,
          },
          filterInfo: {
            include: false,
            filtertype: 'EQUAL',
            datatype: convertDataType(col.dataType),
            value: '',
          },
        };
      });

      setCheckedItems(() => tableItems);

      // reset the metrics
      if (table.reset) {
        setMetricItems([]);
        setDimItems([]);
        setFilterItems([]);
      }

      // Successfully loaded columns
    } catch (error) {
      console.error(
        '❌ GetTableColumnsFromStorage: Error loading table columns:',
        error,
      );

      // Fallback to default table structure
      const defaultTableItems: ICheckedTable[] = [
        {
          datatype: 'STRING',
          name: 'default_column',
          checked: false,
          dimension: false,
          filter: false,
          filterList: new Set(),
          metric: {
            include: false,
            pretty: 'Default Column',
            method: 'SUM',
          },
          filterInfo: {
            include: false,
            filtertype: 'EQUAL',
            datatype: 'STRING',
            value: '',
          },
        },
      ];
      setCheckedItems(defaultTableItems);
    }
  }, []);

  const selectNewTable = useCallback(
    (id: string, resetCols: boolean) => {
      const allTables: ITable[] = tables.map((t) => ({
        ...t,
        selected: t.id === id,
      }));

      const selectedTable = allTables.find((t) => t.selected);
      GetTableColumnsFromStorage(
        selectedTable || {
          reset: false,
          id: 'NA',
          selected: false,
          caption: 'NA',
          source: 'NA',
          view: 'NA',
          group: 'NA',
        },
      );
      setTables(() => allTables);

      if (resetCols) {
        console.log('resetCols... cols should be reset');
        resetItems();
      }
    },
    [GetTableColumnsFromStorage, tables],
  );

  const UpdateAvailableClients = useCallback(
    (allUserClients: IClient[], country: string) => {
      // Filter by country only - let ClientSelector handle agency filtering via search
      const ss = allUserClients.filter(
        (c) => c.country === country,
      );

      const unknownClient = {
        client_code: 'UNKNOWN',
        client_name: 'UNKNOWN',
        country,
        agency: 'UNKNOWN', // Keep agency field but set to UNKNOWN
        selected: false,
      };

      ss.push(unknownClient);

      setClients(ss);

      // const newDatabase = getDataBaseByCountry(country);
      // setDatabase(() => newDatabase);

      return ss;
    },
    [],
  );

  const updateQuery = useCallback(
    (updatedQuery: IUpdatedQuery) => {
      console.log('Update query called', updatedQuery);
      const {
        views,
        clients: queryClients,
        query,
        user: QueryUser,
      } = updatedQuery;
      const [{ viewName }] = views;

      const { agency, country } = QueryUser;

      const updatedClients = UpdateAvailableClients(
        allClients,
        country,
      );

      // console.log('Update query called - viewName', viewName);

      // remove "agency" to get the view prefix
      const n = viewName.lastIndexOf('_');
      const res = viewName.substring(0, n + 1);

      const newTable = tables.find((t) => t.view === res);
      if (!newTable) {
        console.error(`No Tables found with view ${res}`, tables);

        throw new Error(`No Tables found with view ${res}`);
      }

      selectNewTable(newTable.id, false);

      const newClients = queryClients.map((cl) => {
        const { client } = cl;
        return client;
      });

      // console.log('Update query called - new clients', newClients);
      // console.log('Update query called - updated clients', updatedClients);

      const ss = updatedClients.map((client) => {
        if (newClients.includes(client.client_code)) {
          return { ...client, selected: true };
        }
        return client;
      });
      // console.log('Update query called - selected clients', ss);

      setClients(() => ss);
      // console.log('Update query called - country and agency', country, agency);
      selectAgency(agency);
      selectCountry(country);

      const { metrics, dimensions, filters } = query;

      const newMetrics = metrics.map((m: IApiMetric) => {
        const { aggregation, ...rest } = m;
        return {
          id: uuidv4(),
          itemType: ItemTypes.METRIC,
          aggregationMethod: aggregation,
          ...rest,
        };
      });
      // console.log('Update query called - newMetrics', newMetrics);
      setMetricItems(() => newMetrics);

      // console.log('Update query called - dimensions', dimensions);

      // console.log('Update query called - dimItems', dimItems);
      const dimValues: INewDimension[] = dimensions.map((d) => ({
        dimension: d,
        id: d,
        datatype: 'STRING',
        name: d,
        itemType: ItemTypes.DIM,
      }));
      // console.log('Update query called - dimensions', dimValues);

      setDimItems(() => dimValues);

      // console.log('Update query called - filters', filters);
      // console.log('Update query called - filterItems', filterItems);
      const newFilters = filters.map((m) => {
        const { ...rest } = m;
        return {
          ...rest,
          id: uuidv4(),
        };
      });
      // console.log('Update query called - newFilters', newFilters);
      setFilterItems(newFilters);
    },
    [
      allClients,
      selectAgency,
      selectCountry,
      selectNewTable,
      tables,
      UpdateAvailableClients,
    ],
  );
  function getDataBaseByCountry(country: string) {
    let newDatabase = 'data_lake';
    switch (country) {
      case 'DK':
        newDatabase = 'data_lake_denmark';
        break;
      case 'SE':
        newDatabase = 'data_lake_sweden';
        break;
      case 'NO':
        newDatabase = 'data_lake_norway';
        break;
      default:
        break;
    }
    return newDatabase;
  }

  const toggleClient = useCallback((clientCode: string, country: string) => {
    setClients((prevClients) =>
      prevClients.map((client) => {
        if (client.client_code === clientCode && client.country === country) {
          const { selected } = client;
          return { ...client, selected: !selected };
        }
        return client;
      }),
    );
  }, []);

  const foundCountry = countries.find((s) => s.selected);
  if (!foundCountry) {
    const errorMessage = `No selected country found`;
    console.error(errorMessage, countries);
    throw new Error(errorMessage);
  }
  const selectedCountry = foundCountry;

  const foundAgency = agencies.find((s) => s.selected);
  if (!foundAgency) {
    const errorMessage = `No selected agency found`;
    console.error(errorMessage, agencies);
    throw new Error(errorMessage);
  }
  const selectedAgency = foundAgency;

  const selectedTable = useMemo(() => {
    const foundTable = tables.find((t) => t.selected);

    if (!foundTable) {
      const errorMessage = `No selected table found`;
      console.error(errorMessage, tables);
      throw new Error(errorMessage);
    }

    return {
      table: foundTable,
      reset: true,
    };
  }, [tables]);

  const getQueryInfo = useCallback(
    (previewCount: number) => {
      // Update Clients
      const sc = selectedClients.map((client) => ({
        client: client.client_code,
        country: client.country,
      }));

      // Update Views
      // Derive agency from selected clients (take the first selected client's agency)
      const agency = selectedClients.length > 0 ? selectedClients[0].agency : 'notset';
      const country = selectedCountry.value;
      const agencyLower = agency ? agency.toLowerCase() : 'notset';

      const newViewName = selectedTable.table.view + agencyLower;

      const newView = [
        { viewName: newViewName, database: getDataBaseByCountry(country) },
      ];

      // Update query

      const metrics = metricItems.map((item) => {
        const { aggregationMethod: aggregation, ...others } = item;
        // Omit id, datatype, itemType
        const { id, datatype, itemType, ...filteredOthers } = others;
        return { aggregation, ...filteredOthers };
      });

      const filters = filterItems.map((item) => {
        // Omit id, datatype, itemType
        const { id, datatype, itemType, ...others } = item;
        return { ...others };
      });

      const query = {
        preview: previewCount,
        metrics,
        filters,
        dimensions: dimItems.map((d) => d.dimension),
      };

      // Update User
      const user = {
        name: userName,
        country: selectedCountry.value, // Extract the enum value, not the object
        agency: agency, // Use derived agency from selected clients
      };

      // Update Dates + User (extract directly from states)

      const newQ = {
        ...newQueryInput,
        query,
        user,
        clients: sc,
        views: newView,
        date: selectedDates,
      };
      console.log('Query: ', newQ);

      return newQ;
    },
    [
      dimItems,
      filterItems,
      metricItems,
      selectedClients,
      selectedCountry,
      selectedDates,
      selectedTable.table.view,
      userName,
    ],
  );

  const removeDim = useCallback((dim: string) => {
    setDimItems((prevDimItems) =>
      prevDimItems.filter((t) => t.dimension !== dim),
    );
  }, []);

  const removeFilter = useCallback((id: string) => {
    setFilterItems((prevFilterItems) =>
      prevFilterItems.filter((t) => t.id !== id),
    );
  }, []);

  const removeMetric = useCallback((id: string) => {
    setMetricItems((prevMetricItems) =>
      prevMetricItems.filter((t) => t.id !== id),
    );
  }, []);

  const removeDimensions = useCallback(
    (id: string, itemType: string) => {
      // console.log(id, itemType);
      switch (itemType) {
        case ItemTypes.DIM: {
          removeDim(id);
          break;
        }
        case ItemTypes.FILTER: {
          removeFilter(id);
          break;
        }
        case ItemTypes.METRIC: {
          removeMetric(id);
          break;
        }

        default:
          break;
      }
    },
    [removeDim, removeFilter, removeMetric],
  );

  const addDimension = useCallback((dim: string) => {
    setDimItems((prev) => [
      ...prev,
      {
        dimension: dim,
        id: dim,
        datatype: 'STRING',
        name: dim,
        itemType: ItemTypes.DIM,
      },
    ]);
  }, []);

  const getDefaultValue = (datatype: string) => {
    switch (datatype) {
      case 'STRING':
        return '';
      case 'NUMBER':
        return '0';
      case 'DATE':
        return '2020-01-01';
      default:
        return 'default';
    }
  };

  const replaceVal = (f: INewFilter, val: string) => {
    const { filter, ...item } = f;
    const { value, ...rest } = filter;
    const nf = { ...rest, value: val };
    // console.log('nf', {...item, filter: nf})
    return { ...item, filter: nf };
  };
  const replaceFilterType = (f: INewFilter, val: string) => {
    const { filter, ...item } = f;
    const { filtertype, ...rest } = filter;
    const nf = { ...rest, filtertype: val };
    return { ...item, filter: nf };
  };
  const updateFilter = useCallback(
    (val: string, id: string, itemMethod: string) => {
      setFilterItems((prev) =>
        prev.map((el) =>
          el.id === id
            ? itemMethod === 'value'
              ? replaceVal(el, val)
              : replaceFilterType(el, val)
            : el,
        ),
      );
    },
    [],
  );

  const addFilter = useCallback((item: IItem, id: string) => {
    // console.log(item,id)
    // first make sure id is not present already
    setFilterItems((prevFilterItems) => {
      if (!prevFilterItems.some((el) => el.id === id)) {
        const newItem: INewFilter = {
          column: item.column,
          id,
          filter: {
            datatype: item.datatype,
            filtertype: 'EQUAL',
            value: getDefaultValue(item.datatype),
          },
          datatype: item.datatype,
          itemType: ItemTypes.FILTER,
        };
        // console.log('newItem', newItem)

        return [...prevFilterItems, newItem];
      }
      return prevFilterItems;
    });
  }, []);

  const updatePrettyName = useCallback((id: string, val: string) => {
    // console.log('Update Pretty Name', val, id);
    setMetricItems((prevMetricItems) =>
      prevMetricItems.map((el) => (el.id === id ? { ...el, name: val } : el)),
    );
  }, []);

  const updateAggMethod = useCallback((id: string, val: string) => {
    // console.log('Update aggMethod', val, id);
    setMetricItems((prevMetricItems) =>
      prevMetricItems.map((el) =>
        el.id === id ? { ...el, aggregationMethod: val } : el,
      ),
    );
  }, []);
  const updateMetric = useCallback(
    (updateType: string, id: string, val: string) => {
      switch (updateType) {
        case ACTIONS.UPDATE.METRIC.AGGREGATION_METHOD:
          updateAggMethod(id, val);
          break;
        case ACTIONS.UPDATE.METRIC.PRETTY_NAME:
          updatePrettyName(id, val);
          break;
        default:
          console.log('Unknown Metric Update');
          break;
      }
    },
    [updateAggMethod, updatePrettyName],
  );

  const findIndexById = useCallback((indexType: string, id: string) => {
    switch (indexType) {
      case ItemTypes.DIM:
        return dimItems.findIndex((item) => item.id === id);
      case ItemTypes.FILTER:
        return filterItems.findIndex((item) => item.id === id);
      case ItemTypes.METRIC:
        return metricItems.findIndex((item) => item.id === id);
      default:
        console.error(`Unknown index type: ${indexType}`);
        return null;
    }
  }, [dimItems, filterItems, metricItems]);

  const moveCard = useCallback(
    (id: string, atIndex: number, indexType: string) => {
      switch (indexType) {
        case ItemTypes.DIM:
          setDimItems((prevItems) => {
            const card = prevItems.find((c) => c.id === id);
            if (!card) {
              console.error(`No item found with ${id}`, prevItems);
              return prevItems;
            }
            const index = prevItems.indexOf(card);
            return move([...prevItems], index, atIndex);
          });
          break;
        case ItemTypes.FILTER:
          setFilterItems((prevItems) => {
            const card = prevItems.find((c) => c.id === id);
            if (!card) {
              console.error(`No item found with ${id}`, prevItems);
              return prevItems;
            }
            const index = prevItems.indexOf(card);
            return move([...prevItems], index, atIndex);
          });
          break;
        case ItemTypes.METRIC:
          setMetricItems((prevItems) => {
            const card = prevItems.find((c) => c.id === id);
            if (!card) {
              console.error(`No item found with ${id}`, prevItems);
              return prevItems;
            }
            const index = prevItems.indexOf(card);
            return move([...prevItems], index, atIndex);
          });
          break;
        default:
          alert('Unknown index type');
      }
    },
    [],
  );

  const resetClients = useCallback(
    (cognitoUser: ICognitoUser): ICognitoClient[] => {
      console.log('🔄 resetClients: Function called with user:', cognitoUser || 'no user');
      console.log('🔄 resetClients: Access token present:', !!cognitoUser?.accessToken);
      console.log('🔄 resetClients: Campaign Mapping URL:', process.env.REACT_APP_CAMPAIGN_MAPPING_BASE_URL);
      console.log('🔄 resetClients: Current window location:', window.location.href);
      console.log('🔄 resetClients: Environment check - is staging?', window.location.href.includes('staging'));

      const campaignMapping = axios.create({
        baseURL: process.env.REACT_APP_CAMPAIGN_MAPPING_BASE_URL,
        timeout: 15000, // Increased timeout for staging
        headers: {
          Authorization: `Bearer ${cognitoUser.accessToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'Data-Explorer-Staging/1.0',
        },
        // Enhanced CORS configuration for staging
        withCredentials: false,
        // Add request interceptor for debugging
        validateStatus: function (status) {
          return status >= 200 && status < 500; // Don't reject on 4xx errors
        },
      });

      // Add request interceptor for debugging
      campaignMapping.interceptors.request.use(
        (config) => {
          console.log('🌐 AXIOS REQUEST CONFIG:', {
            url: config.url,
            baseURL: config.baseURL,
            method: config.method,
            headers: config.headers,
            timeout: config.timeout,
          });
          return config;
        },
        (error) => {
          console.error('🚨 AXIOS REQUEST ERROR:', error);
          return Promise.reject(error);
        }
      );

      // Add response interceptor for debugging
      campaignMapping.interceptors.response.use(
        (response) => {
          console.log('✅ AXIOS RESPONSE SUCCESS:', {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            dataType: typeof response.data,
            dataLength: response.data?.length || 'unknown',
          });
          return response;
        },
        (error) => {
          console.error('❌ AXIOS RESPONSE ERROR:', {
            message: error.message,
            code: error.code,
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            config: error.config,
          });
          return Promise.reject(error);
        }
      );

      let allUserClients: ICognitoClient[] = [];

      console.log('🌐 resetClients: About to make axios GET request to Campaign Mapping API...');
      console.log('🌐 resetClients: Full URL will be:', `${process.env.REACT_APP_CAMPAIGN_MAPPING_BASE_URL}clients`);
      console.log('🌐 resetClients: Authorization header:', `Bearer ${cognitoUser.accessToken?.substring(0, 50)}...`);
      console.log('🌐 resetClients: Environment details:', {
        location: window.location.href,
        origin: window.location.origin,
        userAgent: navigator.userAgent,
        isStaging: window.location.href.includes('staging'),
      });

      // Add a timeout promise to detect hanging requests
      const apiCall = campaignMapping.get('clients');

      console.log('🌐 resetClients: API call promise created, waiting for response...');
      console.log('🌐 resetClients: Starting network request timestamp:', new Date().toISOString());

      // Create a timeout promise for debugging hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Custom timeout after 20 seconds - network may be blocked'));
        }, 20000);
      });

      // Race between API call and timeout
      Promise.race([apiCall, timeoutPromise])
        .then((response) => {
          console.log('✅ resetClients: Promise resolved successfully!');
          console.log('📊 Campaign Mapping API Response:', response);

          // Type assertion for axios response
          const axiosResponse = response as { data: { clients?: IClientInfo[] } };
          console.log('📊 Response data:', axiosResponse.data);

          // Check if the response is HTML instead of JSON (common CORS/routing issue)
          const responseDataString = axiosResponse.data as unknown as string;
          if (typeof responseDataString === 'string' && responseDataString.includes('<!DOCTYPE html>')) {
            console.error('🚨 CRITICAL: API returned HTML instead of JSON - This indicates a server routing/CORS issue');
            console.error('🔧 The staging server is redirecting API calls back to the React app');
            console.error('🔧 Backend team needs to:');
            console.error('   1. Configure CORS to allow staging domain');
            console.error('   2. Ensure API routing doesn\'t redirect to React app');
            console.error('   3. Check proxy/load balancer configuration');
            throw new Error('API returned HTML instead of JSON - Server configuration issue');
          }

          // Check if the response has the expected structure
          if (!axiosResponse.data || !axiosResponse.data.clients) {
            console.error('❌ API response missing clients data:', axiosResponse.data);
            throw new Error('API response missing clients data');
          }

          const newClients = axiosResponse.data.clients;
          console.log('✅ Found clients:', newClients);

          if (!Array.isArray(newClients)) {
            console.error('❌ Clients data is not an array:', typeof newClients);
            throw new Error('Clients data is not an array');
          }

          allUserClients = newClients.map((c: IClientInfo) => ({
            code: c.clientCode.client, // Required for ICognitoClient interface
            client_code: c.clientCode.client,
            client_name: c.clientName,
            country: c.clientCode.country,
            agency: c.agency,
            selected: false,
          }));

          const userWithAgencyAndCountry: ICognitoUser = {
            ...cognitoUser,
            allUserClients,
          };

          const ss = signIn(userWithAgencyAndCountry);

          const { country, userClients } = ss;

          UpdateAvailableClients(userClients, country);
        })
        .catch((error) => {
          console.log('❌ resetClients: Promise rejected!');
          console.error('❌ resetClients: API call failed:', error);
          console.log('❌ resetClients: Error type:', error.constructor.name);
          console.log('❌ resetClients: Error code:', error.code);
          console.log('❌ resetClients: Error message:', error.message);
          console.log('❌ resetClients: Network error?', error.code === 'ERR_NETWORK');
          console.log('❌ resetClients: Timeout error?', error.code === 'ECONNABORTED');
          console.log('❌ resetClients: Response status:', error.response?.status);
          console.log('❌ resetClients: Response data:', error.response?.data);

          // Detailed error analysis
          if (error.message && error.message.includes('HTML instead of JSON')) {
            console.log('🚨 SERVER ROUTING ISSUE: Staging server redirecting API calls to React app');
          } else if (error.message && error.message.includes('CORS')) {
            console.log('🚨 CORS Error detected - Backend team needs to whitelist staging domain');
          } else if (error.code === 'ERR_NETWORK') {
            console.log('🚨 Network error - API may be unreachable from staging environment');
          } else if (error.response?.status === 401) {
            console.log('🚨 Authentication error - Token may be invalid for external API');
          } else if (error.response?.status === 403) {
            console.log('🚨 Authorization error - User may not have permissions');
          } else {
            console.log('🚨 Unknown API error:', error.code, error.response?.status);
          }

          // For staging environment - create fallback client data
          console.log('⚠️ Using fallback client data for staging environment');
          console.log('💡 This allows testing while backend issues are resolved');

          // Create fallback clients based on user email domain for testing
          const fallbackClients: ICognitoClient[] = [
            {
              code: 'TEST_CLIENT_DK',
              client_code: 'TEST_CLIENT_DK',
              client_name: 'Test Client Denmark',
              country: 'DK',
              agency: 'OMD',
              selected: false,
            },
            {
              code: 'TEST_CLIENT_NO',
              client_code: 'TEST_CLIENT_NO',
              client_name: 'Test Client Norway',
              country: 'NO',
              agency: 'OMD',
              selected: false,
            },
            {
              code: 'TEST_CLIENT_SE',
              client_code: 'TEST_CLIENT_SE',
              client_name: 'Test Client Sweden',
              country: 'SE',
              agency: 'PHD',
              selected: false,
            },
          ];

          allUserClients = fallbackClients;

          const userWithAgencyAndCountry: ICognitoUser = {
            ...cognitoUser,
            allUserClients: fallbackClients,
          };

          try {
            const ss = signIn(userWithAgencyAndCountry);
            const { country, userClients } = ss;
            UpdateAvailableClients(userClients, country);
            console.log('✅ Successfully applied fallback client data');
          } catch (signInError) {
            console.error('❌ signIn failed with fallback data:', signInError);
          }
        });
      return allUserClients;
    },
    [signIn, UpdateAvailableClients],
  );

  const userSignIn = useCallback(
    (newUser: ICognitoUser) => {
      console.log('🔑 QueryBuilderProvider.userSignIn: Called with user:', newUser || 'no user object');
      console.log('🔑 QueryBuilderProvider.userSignIn: Access token present:', !!newUser?.accessToken);

      // Create user object from amplify Cognito userData so we only have to navigate it once
      resetItems();

      console.log('🔑 QueryBuilderProvider.userSignIn: About to call resetClients...');
      resetClients(newUser);
      console.log('🔑 QueryBuilderProvider.userSignIn: resetClients call completed');
    },
    [resetClients],
  );

  function signedInFrom() {
    setSignInFrom(() => '/TV');
  }

  useEffect(() => {
    async function GetTables(source: string) {
      // Attempting to load tables from S3

      try {
        // Use the new S3 table loader that bypasses Papa Parse
        const tablesFromS3 = await loadTablesFromS3(source);

        if (tablesFromS3 && tablesFromS3.length > 0) {
          // Successfully loaded tables from S3

          // Ensure at least one table is selected
          const allTables = tablesFromS3.map((t, index) => ({
            ...t,
            selected: index === 0, // Select first table by default
          }));

          const selectedTable = allTables.find((t) => t.selected);
          if (selectedTable) {
            GetTableColumnsFromStorage(selectedTable);
          }

          setTables(allTables);
          return;
        }

        // If S3 loading failed, use comprehensive default tables
        console.warn(
          'Failed to load tables from S3, using comprehensive default tables',
        );
      } catch (error) {
        console.error('❌ GetTables: Error loading tables from S3:', error);
      }

      // Fallback to comprehensive default tables
      console.warn('⚡ GetTables: Using comprehensive fallback tables');
      const comprehensiveDefaultTables: ITable[] = [
        // Adform group
        {
          id: '1',
          caption: 'adform',
          source: 'adform.csv',
          view: 'dl_adform_',
          group: 'adform',
          selected: true,
        },
        {
          id: '2',
          caption: 'tracking data',
          source: 'adform_tracking_data.csv',
          view: 'dl_adform_tracking_data_',
          group: 'adform',
          selected: false,
        },

        // Facebook group
        {
          id: '3',
          caption: 'facebook ads',
          source: 'facebook_ads.csv',
          view: 'dl_facebook_ads_',
          group: 'facebook',
          selected: false,
        },
        {
          id: '4',
          caption: 'facebook insights',
          source: 'facebook_insights.csv',
          view: 'dl_facebook_insights_',
          group: 'facebook',
          selected: false,
        },

        // Google group
        {
          id: '5',
          caption: 'google ads',
          source: 'google_ads.csv',
          view: 'dl_google_ads_',
          group: 'google',
          selected: false,
        },
        {
          id: '6',
          caption: 'google analytics',
          source: 'google_analytics.csv',
          view: 'dl_google_analytics_',
          group: 'google',
          selected: false,
        },

        // DV360 group
        {
          id: '7',
          caption: 'dv360 campaigns',
          source: 'dv360_campaigns.csv',
          view: 'dl_dv360_campaigns_',
          group: 'dv360',
          selected: false,
        },

        // Amazon group
        {
          id: '8',
          caption: 'amazon dsp',
          source: 'amazon_dsp.csv',
          view: 'dl_amazon_dsp_',
          group: 'amazon',
          selected: false,
        },

        // TV & Audio group
        {
          id: '9',
          caption: 'tv viewership',
          source: 'tv_viewership.csv',
          view: 'dl_tv_viewership_',
          group: 'tv_audio',
          selected: false,
        },
      ];

      setTables(comprehensiveDefaultTables);
      const selectedTable = comprehensiveDefaultTables.find((t) => t.selected);
      if (selectedTable) {
        GetTableColumnsFromStorage(selectedTable);
      }
    }
    const go = async () => {
      // Only try to fetch from S3 if user is logged in
      if (!loggedIn) {
        console.log('User not logged in, using comprehensive default tables');
        // Use comprehensive default tables for unauthenticated users
        const comprehensiveDefaultTables: ITable[] = [
          // Adform group
          {
            id: '1',
            caption: 'adform',
            source: 'adform.csv',
            view: 'dl_adform_',
            group: 'adform',
            selected: true,
          },
          {
            id: '2',
            caption: 'tracking data',
            source: 'adform_tracking_data.csv',
            view: 'dl_adform_tracking_data_',
            group: 'adform',
            selected: false,
          },

          // Facebook group
          {
            id: '3',
            caption: 'facebook ads',
            source: 'facebook_ads.csv',
            view: 'dl_facebook_ads_',
            group: 'facebook',
            selected: false,
          },
          {
            id: '4',
            caption: 'facebook insights',
            source: 'facebook_insights.csv',
            view: 'dl_facebook_insights_',
            group: 'facebook',
            selected: false,
          },

          // Google group
          {
            id: '5',
            caption: 'google ads',
            source: 'google_ads.csv',
            view: 'dl_google_ads_',
            group: 'google',
            selected: false,
          },
          {
            id: '6',
            caption: 'google analytics',
            source: 'google_analytics.csv',
            view: 'dl_google_analytics_',
            group: 'google',
            selected: false,
          },
          {
            id: '7',
            caption: 'google search console',
            source: 'google_search_console.csv',
            view: 'dl_google_search_console_',
            group: 'google',
            selected: false,
          },

          // DV360 group
          {
            id: '8',
            caption: 'dv360 campaigns',
            source: 'dv360_campaigns.csv',
            view: 'dl_dv360_campaigns_',
            group: 'dv360',
            selected: false,
          },
          {
            id: '9',
            caption: 'dv360 creative',
            source: 'dv360_creative.csv',
            view: 'dl_dv360_creative_',
            group: 'dv360',
            selected: false,
          },

          // Amazon group
          {
            id: '10',
            caption: 'amazon dsp',
            source: 'amazon_dsp.csv',
            view: 'dl_amazon_dsp_',
            group: 'amazon',
            selected: false,
          },
          {
            id: '11',
            caption: 'amazon advertising',
            source: 'amazon_advertising.csv',
            view: 'dl_amazon_advertising_',
            group: 'amazon',
            selected: false,
          },

          // TikTok group
          {
            id: '12',
            caption: 'tiktok ads',
            source: 'tiktok_ads.csv',
            view: 'dl_tiktok_ads_',
            group: 'tiktok',
            selected: false,
          },

          // LinkedIn group
          {
            id: '13',
            caption: 'linkedin ads',
            source: 'linkedin_ads.csv',
            view: 'dl_linkedin_ads_',
            group: 'linkedin',
            selected: false,
          },

          // TV & Audio group
          {
            id: '14',
            caption: 'tv viewership',
            source: 'tv_viewership.csv',
            view: 'dl_tv_viewership_',
            group: 'tv_audio',
            selected: false,
          },
          {
            id: '15',
            caption: 'radio listening',
            source: 'radio_listening.csv',
            view: 'dl_radio_listening_',
            group: 'tv_audio',
            selected: false,
          },

          // E-commerce group
          {
            id: '16',
            caption: 'shopify sales',
            source: 'shopify_sales.csv',
            view: 'dl_shopify_sales_',
            group: 'ecommerce',
            selected: false,
          },
          {
            id: '17',
            caption: 'magento orders',
            source: 'magento_orders.csv',
            view: 'dl_magento_orders_',
            group: 'ecommerce',
            selected: false,
          },
        ];

        setTables(comprehensiveDefaultTables);

        // Call GetTableColumnsFromStorage for the selected table with fallback
        const selectedTable = comprehensiveDefaultTables.find(
          (t) => t.selected,
        );
        if (selectedTable) {
          GetTableColumnsFromStorage(selectedTable);
        }
        return;
      }

      // Try to get remote table data, fall back to defaults if it fails
      try {
        console.log(
          'User is logged in, attempting to load remote tables from storage...',
        );
        await GetTables('tables.csv');
      } catch (error) {
        console.warn(
          'Failed to load remote tables, using comprehensive defaults:',
          error,
        );
        // Use a more comprehensive set of default tables with multiple groups
        const comprehensiveDefaultTables: ITable[] = [
          // Adform group
          {
            id: '1',
            caption: 'adform',
            source: 'adform.csv',
            view: 'dl_adform_',
            group: 'adform',
            selected: true,
          },
          {
            id: '2',
            caption: 'tracking data',
            source: 'adform_tracking_data.csv',
            view: 'dl_adform_tracking_data_',
            group: 'adform',
            selected: false,
          },

          // Facebook group
          {
            id: '3',
            caption: 'facebook ads',
            source: 'facebook_ads.csv',
            view: 'dl_facebook_ads_',
            group: 'facebook',
            selected: false,
          },
          {
            id: '4',
            caption: 'facebook insights',
            source: 'facebook_insights.csv',
            view: 'dl_facebook_insights_',
            group: 'facebook',
            selected: false,
          },

          // Google group
          {
            id: '5',
            caption: 'google ads',
            source: 'google_ads.csv',
            view: 'dl_google_ads_',
            group: 'google',
            selected: false,
          },
          {
            id: '6',
            caption: 'google analytics',
            source: 'google_analytics.csv',
            view: 'dl_google_analytics_',
            group: 'google',
            selected: false,
          },
          {
            id: '7',
            caption: 'google search console',
            source: 'google_search_console.csv',
            view: 'dl_google_search_console_',
            group: 'google',
            selected: false,
          },

          // DV360 group
          {
            id: '8',
            caption: 'dv360 campaigns',
            source: 'dv360_campaigns.csv',
            view: 'dl_dv360_campaigns_',
            group: 'dv360',
            selected: false,
          },
          {
            id: '9',
            caption: 'dv360 creative',
            source: 'dv360_creative.csv',
            view: 'dl_dv360_creative_',
            group: 'dv360',
            selected: false,
          },

          // Amazon group
          {
            id: '10',
            caption: 'amazon dsp',
            source: 'amazon_dsp.csv',
            view: 'dl_amazon_dsp_',
            group: 'amazon',
            selected: false,
          },
          {
            id: '11',
            caption: 'amazon advertising',
            source: 'amazon_advertising.csv',
            view: 'dl_amazon_advertising_',
            group: 'amazon',
            selected: false,
          },

          // TikTok group
          {
            id: '12',
            caption: 'tiktok ads',
            source: 'tiktok_ads.csv',
            view: 'dl_tiktok_ads_',
            group: 'tiktok',
            selected: false,
          },

          // LinkedIn group
          {
            id: '13',
            caption: 'linkedin ads',
            source: 'linkedin_ads.csv',
            view: 'dl_linkedin_ads_',
            group: 'linkedin',
            selected: false,
          },

          // TV & Audio group
          {
            id: '14',
            caption: 'tv viewership',
            source: 'tv_viewership.csv',
            view: 'dl_tv_viewership_',
            group: 'tv_audio',
            selected: false,
          },
          {
            id: '15',
            caption: 'radio listening',
            source: 'radio_listening.csv',
            view: 'dl_radio_listening_',
            group: 'tv_audio',
            selected: false,
          },

          // E-commerce group
          {
            id: '16',
            caption: 'shopify sales',
            source: 'shopify_sales.csv',
            view: 'dl_shopify_sales_',
            group: 'ecommerce',
            selected: false,
          },
          {
            id: '17',
            caption: 'magento orders',
            source: 'magento_orders.csv',
            view: 'dl_magento_orders_',
            group: 'ecommerce',
            selected: false,
          },
        ];

        setTables(comprehensiveDefaultTables);

        // Call GetTableColumnsFromStorage for the selected table with fallback
        const selectedTable = comprehensiveDefaultTables.find(
          (t) => t.selected,
        );
        if (selectedTable) {
          GetTableColumnsFromStorage(selectedTable);
        }
      }
    };
    go();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]); // Re-run when login status changes

  // useEffect(() => {
  //   console.log('selected clients called', selectedClients.current);
  //   selectedClients.current = clients.filter((a) => a.selected);
  // }, [clients]);

  const [allowEmptyClientList, setAllowEmptyClientList] = useState(false);

  const isNotReady =
    // selectedClients.current.length === 0 || metricItems.length === 0;

    (selectedClients.length === 0 && allowEmptyClientList !== true) ||
    metricItems.length === 0;

  const findColumn = useCallback(
    (column: string) => {
      const outValue = checkedItems.find((s) => s.name === column);
      if (!outValue) {
        const errorMessage = `No checked item found where name = ${column}`;
        console.error(errorMessage, checkedItems);
        throw new Error(errorMessage);
      }
      // console.log('call in findColumn', column, checkedItems, outValue);
      return outValue;
    },
    [checkedItems],
  );

  const addMetric = useCallback(
    (item: { datatype: string; column: string }, id: string) => {
      const { datatype, column } = item;
      console.log('dataType', datatype);
      if (datatype === 'STRING') {
        const newAlertDescription =
          'It is not possible to add a STRING variable as metric. Use Dimensions instead if you want to group by "' +
          column.toUpperCase() +
          '"';
        const newAlertTitle =
          'Cannot use "' + column.toUpperCase() + '" as a metric';

        setQueryAlert(() => ({
          showAlert: true,
          alertDescription: newAlertDescription,
          alertTitle: newAlertTitle,
        }));

        return;
      }

      // Use functional update to avoid dependency on metricItems
      setMetricItems((prevMetricItems) => {
        // first make sure id is not present already
        if (!prevMetricItems.some((el) => el.id === id)) {
          console.log('column:', column, id, prevMetricItems);
          const { metric } = findColumn(column);
          const newItem: INewMetric = {
            id,
            column,
            aggregationMethod: metric.method,
            datatype,
            name: metric.pretty,
            itemType: ItemTypes.METRIC,
          };
          console.log('newItem', newItem);
          return [...prevMetricItems, newItem];
        }
        return prevMetricItems;
      });
    },
    [findColumn],
  );

  const foo = useMemo(
    () => ({
      clients,
      checkedItems,
      metricItems,
      dimItems,
      filterItems,
      addMetric,
      addDimension,
      addFilter,
      removeDimensions,
      findColumn,
      queryAlert,
      resetQueryAlert,
      GetTableColumnsFromStorage,
      getDataBaseByCountry,
      updateMetric,
      updateFilter,
      moveCard,
      resetItems,
      updateQuery,
      getQueryInfo,
      newQueryInput,
      isNotReady,
      findIndexById,
      tables,
      selectedAgency,
      selectedCountry,
      selectedTable,
      selectedClients,
      selectedDates,
      setSelectedDates,
      userSignIn,
      toggleClient,
      UpdateAvailableClients,
      selectNewTable,
      signInFrom,
      signedInFrom,
      allowEmptyClientList,
      setAllowEmptyClientList,
    }),
    [
      GetTableColumnsFromStorage,
      addDimension,
      addFilter,
      addMetric,
      allowEmptyClientList,
      checkedItems,
      clients,
      dimItems,
      filterItems,
      findColumn,
      findIndexById,
      getQueryInfo,
      isNotReady,
      metricItems,
      moveCard,
      queryAlert,
      removeDimensions,
      resetQueryAlert,
      selectNewTable,
      selectedAgency,
      selectedClients,
      selectedCountry,
      selectedDates,
      selectedTable,
      signInFrom,
      tables,
      toggleClient,
      updateFilter,
      updateMetric,
      updateQuery,
      userSignIn,
      UpdateAvailableClients,
    ],
  );

  return (
    <QueryBuilderContext.Provider value={foo}>
      {children}
    </QueryBuilderContext.Provider>
  );
};

// export default useGlobal;
